<script setup lang="ts">
  import { usePlanManageStore } from "~/stores/PlanManageStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();
  const planManageStore = usePlanManageStore();
  planManageStore.fetch();

  interface Props {
    enableAllPlan?: boolean; // 講座一覧に全ての講座を表示するかどうか
    defaultPlanId?: string; // 講座一覧のデフォルトの講座ID
    loading?: boolean; // 講座一覧のローディング状態
  }
  const localProps = withDefaults(defineProps<Props>(), {
    enableAllPlan: true,
    defaultPlanId: undefined,
    loading: true,
  });

  const planId = ref<string>(localProps.defaultPlanId ?? (useRoute().query.planId as string));
  const dataVolume = computed({
    get: () => {
      return !localProps.enableAllPlan ? "planBase" : (useRoute().query.dataVolume as string) || "allData";
    },
    set: val => {
      historyStore.to({ query: filterEmptyObject({ ...useRoute().query, dataVolume: val }) });
    },
  });

  const isSp = isSpDisplayRef();

  const getChoices = () => {
    return (planManageStore.plans || []).map(plan => {
      return { planId: plan.planId, planTitle: plan.planTitle };
    });
  };

  const getSelectedPlan = () => {
    return (planManageStore.plans ?? []).find(plan => plan.planId === planId.value);
  };

  const handleSelect = (val: string) => {
    planId.value = val;
    historyStore.to({ query: filterEmptyObject({ ...useRoute().query, planId: planId.value }) });
  };

  onMounted(() => {
    planId.value = (useRoute().query.planId as string) ?? localProps.defaultPlanId;
  });

  watch(
    () => useRoute().query.planId,
    () => {
      planId.value = (useRoute().query.planId as string) ?? localProps.defaultPlanId;
    },
  );
</script>

<template>
  <div class="d-flex align-center ga-4" :class="{ 'flex-row': !isSp, 'flex-column': isSp }">
    <div v-if="enableAllPlan">
      <AtomsChipSwitch
        :is-first="dataVolume === 'allData'"
        label1="全ユーザー"
        label2="講座毎"
        :disabled="loading"
        @update:is-first="val => (dataVolume = !val ? 'planBase' : 'allData')"
      ></AtomsChipSwitch>
    </div>
    <v-menu scroll-strategy="close">
      <template #activator="{ props }">
        <v-btn v-bind="props" :block="isSp" variant="tonal" :disabled="dataVolume == 'allData' || loading">
          <span class="text-white">{{ getSelectedPlan()?.planTitle || "全ての講座" }}</span>
          <v-icon class="text-white" icon="$mdiChevronDown" />
        </v-btn>
      </template>
      <div class="menu-sort-list">
        <div
          v-for="(item, index) in getChoices()"
          :key="index"
          class="menu-sort-list-item"
          :class="{ disabled: item.planId === planId }"
          @click="handleSelect(item.planId)"
        >
          {{ item.planTitle }}
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .menu-sort-list {
    overflow-y: scroll;
    background: rgb(var(--v-theme-monochrome-04));
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For IE and Edge */
  }
  .menu-sort-list-item {
    padding: 8px 12px;
    cursor: pointer;
    color: white;
    text-align: left;
    &.disabled {
      background: rgb(var(--v-theme-monochrome-06));
    }
    &:not(.disabled):hover {
      background-color: rgb(var(--v-theme-monochrome-05));
    }
  }
</style>
